/**
 * Charge Stations page: template MUI components + API.
 * Table with pagination and search by station number/ID; row click opens detail drawer.
 * Supports cross-link from Powerbanks (crossLinkStation in sessionStorage).
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import Alert from '@mui/material/Alert';
import { DataGrid, gridClasses } from '@mui/x-data-grid';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import * as api from '../../modules/api';
import PageContainer from '../mui-crud/components/PageContainer';
import StationDetailDrawer from './StationDetailDrawer';
import DataGridTableToolbar from '../components/DataGridTableToolbar';
import useDataGridLocaleText from '../hooks/useDataGridLocaleText';
import {
  canAccessStation,
  getCurrentEmail,
  getCurrentRole,
  isAdmin,
  isSuperAdmin,
  loadAdminMerchantEmails,
} from '../utils/accessControl';

const CROSS_LINK_KEY = 'crossLinkStation';

const PLACEMENTS = ['UNASSIGNED', 'INSTALLED', 'WAREHOUSE', 'WITH_TECHNICIAN', 'LOST'];

function normalizePlacement(value) {
  const raw = value != null ? String(value).toUpperCase() : '';
  return PLACEMENTS.includes(raw) ? raw : 'INSTALLED';
}

function placementChipColor(placement) {
  switch (placement) {
    case 'INSTALLED': return 'success';
    case 'WAREHOUSE': return 'info';
    case 'WITH_TECHNICIAN': return 'warning';
    case 'LOST': return 'error';
    default: return 'default';
  }
}

function getStationId(st) {
  return st.stationId ?? st.stationID ?? st.stationNumber ?? null;
}

function mapStationsToRows(list) {
  if (!Array.isArray(list)) return [];
  return list.map((st, index) => {
    const id = getStationId(st) || `row-${index}`;
    const stationNumber = st.stationNumber ?? st.station?.stationNumber ?? '—';
    const serialNumber = st.stationId ?? st.stationID ?? st.station?.stationId ?? st.station?.stationID ?? '—';
    const address = st.address ?? st.station?.address ?? '—';
    const additionalAddress = st.additionalAddress ?? st.station?.additionalAddress ?? '—';
    const rawStatus = st.status ?? st.station?.status;
    const statusNormalized = rawStatus != null ? String(rawStatus).toUpperCase() : null;
    const locked = st.locked ?? st.station?.locked;
    const isLocked = Boolean(locked);
    const isConnected = statusNormalized === 'CONNECTED';
    const connectionStatusDisplay = isConnected ? 'connected' : 'disconnected';
    const businessStatusDisplay = isLocked ? 'blocked' : 'active';
    const placement = normalizePlacement(st.placement ?? st.station?.placement);
    return {
      id: String(id),
      stationNumber,
      serialNumber,
      address,
      additionalAddress,
      availability: isLocked ? false : isConnected,
      locked: isLocked,
      connectionStatusDisplay,
      businessStatusDisplay,
      placement,
      _raw: st,
    };
  });
}

export default function StationsPage() {
  const { t } = useTranslation();
  const dataGridLocaleText = useDataGridLocaleText();
  const [rows, setRows] = useState([]);
  const [stationsMap, setStationsMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 20 });
  const [selectedStation, setSelectedStation] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [searchInputValue, setSearchInputValue] = useState('');
  const [searchQuery, setSearchQuery] = useState(null);
  const [merchantEmails, setMerchantEmails] = useState([]);
  const [merchantEmailsReady, setMerchantEmailsReady] = useState(() => !isAdmin(getCurrentRole()));
  const role = getCurrentRole();
  const email = getCurrentEmail();

  useEffect(() => {
    if (!isAdmin(role)) {
      setMerchantEmails([]);
      setMerchantEmailsReady(true);
      return;
    }
    let cancelled = false;
    setMerchantEmailsReady(false);
    loadAdminMerchantEmails(email).then((list) => {
      if (cancelled) return;
      setMerchantEmails(list);
      setMerchantEmailsReady(true);
    });
    return () => { cancelled = true; };
  }, [role, email]);

  const accessOpts = React.useMemo(
    () => ({ role, email, merchantEmails }),
    [role, email, merchantEmails],
  );

  const filterAccessibleStations = useCallback((list) => {
    if (isSuperAdmin(role)) return Array.isArray(list) ? list : [];
    return (Array.isArray(list) ? list : []).filter((st) => canAccessStation(st, accessOpts));
  }, [role, accessOpts]);

  const loadPage = useCallback(async (page, pageSize = 20) => {
    setError(null);
    setLoading(true);
    try {
      const { data } = await api.GetStations(page, pageSize);
      const list = data.stations || [];
      setRows(mapStationsToRows(list));
      const map = {};
      list.forEach((st) => {
        const key = getStationId(st);
        if (key) map[String(key)] = st;
      });
      setStationsMap(map);
      setTotalCount(Number(data.stationCount) ?? list.length);
    } catch (e) {
      setError(e?.message || t('stations.errorLoad'));
      setRows([]);
      setStationsMap({});
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    try {
      if (sessionStorage.getItem(CROSS_LINK_KEY)) return;
    } catch (_) {}
    if (searchQuery != null && searchQuery !== '') return;
    loadPage(paginationModel.page, paginationModel.pageSize);
  }, [paginationModel.page, paginationModel.pageSize, loadPage, searchQuery]);

  useEffect(() => {
    if (!merchantEmailsReady) return;
    let identifier;
    try {
      identifier = sessionStorage.getItem(CROSS_LINK_KEY);
      if (!identifier) return;
      sessionStorage.removeItem(CROSS_LINK_KEY);
    } catch (_) {
      return;
    }
    setError(null);
    setLoading(true);
    setSearchQuery(identifier);
    api
      .findStations(identifier)
      .then((res) => {
        const data = res?.data;
        const list = data != null ? (Array.isArray(data) ? data : [data]) : [];
        const accessible = filterAccessibleStations(list);
        const station = accessible[0];
        if (list.length > 0 && accessible.length === 0) {
          setError(t('access.stationForbidden'));
          setRows([]);
          setStationsMap({});
          setTotalCount(0);
          setSelectedStation(null);
          setDetailOpen(false);
          return;
        }
        if (station) {
          setRows(mapStationsToRows(accessible));
          const map = {};
          accessible.forEach((st) => {
            const k = getStationId(st);
            if (k) map[String(k)] = st;
          });
          setStationsMap(map);
          setTotalCount(accessible.length);
          setSelectedStation(station);
          setDetailOpen(true);
        } else {
          setRows([]);
          setStationsMap({});
          setTotalCount(0);
        }
      })
      .catch((e) => {
        setError(e?.message || t('stations.errorNotFound'));
        setRows([]);
        setStationsMap({});
        setTotalCount(0);
      })
      .finally(() => setLoading(false));
  }, [merchantEmailsReady, filterAccessibleStations, t]);

  const handleRefresh = useCallback(() => {
    if (!loading) loadPage(paginationModel.page, paginationModel.pageSize);
  }, [loading, loadPage, paginationModel.page, paginationModel.pageSize]);

  const handleSearch = useCallback(
    (query) => {
      const q = (query ?? searchInputValue ?? '').toString().trim();
      if (!q) {
        setSearchQuery(null);
        setSearchInputValue('');
        setPaginationModel((prev) => ({ ...prev, page: 0 }));
        loadPage(0, paginationModel.pageSize);
        return;
      }
      setError(null);
      setLoading(true);
      setSearchQuery(q);
      api
        .findStations(q)
        .then((res) => {
          const data = res?.data;
          const list = data != null ? (Array.isArray(data) ? data : [data]) : [];
          const accessible = filterAccessibleStations(list);
          if (list.length > 0 && accessible.length === 0) {
            setError(t('access.stationForbidden'));
            setRows([]);
            setStationsMap({});
            setTotalCount(0);
            return;
          }
          setRows(mapStationsToRows(accessible));
          const map = {};
          accessible.forEach((st) => {
            const k = getStationId(st);
            if (k) map[String(k)] = st;
          });
          setStationsMap(map);
          setTotalCount(accessible.length);
        })
        .catch((e) => {
          setError(e?.message || t('stations.errorNotFound'));
          setRows([]);
          setStationsMap({});
          setTotalCount(0);
        })
        .finally(() => setLoading(false));
    },
    [searchInputValue, loadPage, filterAccessibleStations, t, paginationModel.pageSize],
  );

  const handleClearSearch = useCallback(() => {
    setSearchQuery(null);
    setSearchInputValue('');
    setError(null);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    loadPage(0, paginationModel.pageSize);
  }, [loadPage, paginationModel.pageSize]);

  const handleRowClick = useCallback(
    (params) => {
      const full = stationsMap[params.row.id] || params.row._raw || params.row;
      setSelectedStation(full);
      setDetailOpen(true);
    },
    [stationsMap],
  );

  const columns = React.useMemo(
    () => [
      { field: 'stationNumber', headerName: t('stations.columnStationNumber'), flex: 0.5, minWidth: 100 },
      { field: 'serialNumber', headerName: t('stations.columnSerialNumber'), flex: 0.8, minWidth: 140 },
      { field: 'address', headerName: t('stations.columnAddress'), flex: 1.2, minWidth: 160 },
      { field: 'additionalAddress', headerName: t('stations.columnAdditionalAddress'), flex: 1.2, minWidth: 160 },
      {
        field: 'connectionStatusDisplay',
        headerName: t('stations.columnConnectionStatus'),
        flex: 0.5,
        minWidth: 140,
        renderCell: (params) => {
          const v = params.value;
          const label = v === 'connected' ? t('stations.connected') : t('stations.disconnected');
          const color = v === 'connected' ? 'success' : 'error';
          return (
            <Chip
              label={label}
              color={color}
              size="small"
            />
          );
        },
      },
      {
        field: 'businessStatusDisplay',
        headerName: t('stations.columnBusinessStatus'),
        flex: 0.5,
        minWidth: 140,
        renderCell: (params) => {
          const v = params.value;
          const label = v === 'blocked' ? t('stations.blocked') : t('stations.active');
          const color = v === 'blocked' ? 'error' : 'success';
          return (
            <Chip
              label={label}
              color={color}
              size="small"
            />
          );
        },
      },
      {
        field: 'placement',
        headerName: t('stations.columnPlacement'),
        flex: 0.6,
        minWidth: 140,
        renderCell: (params) => (
          <Chip
            label={t(`stations.placement_${params.value}`)}
            color={placementChipColor(params.value)}
            size="small"
          />
        ),
      },
    ],
    [t],
  );

  return (
    <Box sx={{ width: '100%' }}>
      <PageContainer
        title={t('stations.title')}
        breadcrumbs={[]}
        actions={
          <Stack direction="row" alignItems="center" spacing={1}>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              component="a"
              href="#/station/new"
            >
              {t('common.create')}
            </Button>
            {searchQuery != null && (
              <Chip label={t('common.clearSearch')} onDelete={handleClearSearch} size="small" sx={{ mr: 0.5 }} />
            )}
            <Tooltip title={t('common.reloadData')} placement="right" enterDelay={1000}>
              <div>
                <IconButton size="small" aria-label="refresh" onClick={handleRefresh}>
                  <RefreshIcon />
                </IconButton>
              </div>
            </Tooltip>
          </Stack>
        }
      >
        <Box sx={{ flex: 1, width: '100%' }}>
          {error ? (
            <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
          ) : (
            <DataGrid
              rows={rows}
              columns={columns}
              loading={loading}
              rowCount={totalCount || rows.length}
              paginationMode="server"
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              pageSizeOptions={[10, 20, 50, 100]}
              onRowClick={handleRowClick}
              localeText={dataGridLocaleText}
              getRowClassName={(params) =>
                params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
              }
              initialState={{ pagination: { paginationModel: { pageSize: 20 } } }}
              disableColumnResize
              density="compact"
              showToolbar
              slots={{ toolbar: DataGridTableToolbar }}
              slotProps={{
                pagination: {
                  labelRowsPerPage: t('common.rowsPerPage'),
                },
                toolbar: {
                  searchValue: searchInputValue,
                  onSearchChange: setSearchInputValue,
                  onSearchSubmit: handleSearch,
                  searchPlaceholder: t('stations.searchPlaceholder'),
                  loading,
                },
                loadingOverlay: { variant: 'circular-progress', noRowsVariant: 'circular-progress' },
                baseIconButton: { size: 'small' },
              }}
              sx={{
                [`& .${gridClasses.columnHeader}, & .${gridClasses.cell}`]: { outline: 'transparent' },
                [`& .${gridClasses.columnHeader}:focus-within, & .${gridClasses.cell}:focus-within`]: { outline: 'none' },
                [`& .${gridClasses.row}`]: { cursor: 'pointer' },
              }}
            />
          )}
        </Box>
      </PageContainer>
      <StationDetailDrawer
        station={selectedStation}
        open={detailOpen}
        onClose={() => { setDetailOpen(false); setSelectedStation(null); }}
        onRefresh={handleRefresh}
        canManage={selectedStation ? canAccessStation(selectedStation, accessOpts) : false}
      />
    </Box>
  );
}
